import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Autocomplete,
  TextField,
  ListSubheader,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import { TIMEZONE_GROUPS, getAllTimezones } from '../../utils/timezones';
import { detectTimezone } from '../../utils/browserDetection';

function PreferencesStep({ formData, updateFormData, onValidationChange }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const detectedTimezone = useMemo(() => detectTimezone(), []);
  const allTimezones = useMemo(() => getAllTimezones(), []);

  // Check if detected timezone is in our list
  const detectedInList = allTimezones.some(tz => tz.value === detectedTimezone);

  const validateForm = () => {
    // Check all required fields
    const isValid =
      formData.timezone &&
      formData.preferredUnit &&
      formData.language;

    // Report validation state to parent (for button control)
    if (onValidationChange) {
      onValidationChange(isValid);
    }

    return isValid;
  };

  // Validate on mount and when form data changes
  useEffect(() => {
    validateForm();
  }, [formData.timezone, formData.preferredUnit, formData.language]);

  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    updateFormData({ language: newLang });
    // Also change the app language immediately
    i18n.changeLanguage(newLang);
  };

  // Find selected timezone object
  const selectedTimezone = allTimezones.find(tz => tz.value === formData.timezone) || null;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('onboarding.preferences.heading')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('onboarding.preferences.description')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Timezone */}
        <FormControl fullWidth>
          <Autocomplete
            options={allTimezones}
            groupBy={(option) => option.group}
            getOptionLabel={(option) => `${option.label}`}
            value={selectedTimezone}
            onChange={(event, newValue) => {
              updateFormData({ timezone: newValue?.value || '' });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('onboarding.preferences.timezone')}
              />
            )}
            renderGroup={(params) => (
              <li key={params.key}>
                <ListSubheader
                  sx={{
                    bgcolor: 'background.paper',
                    fontWeight: 'bold',
                    color: 'primary.main',
                  }}
                >
                  {params.group}
                </ListSubheader>
                <ul style={{ padding: 0 }}>{params.children}</ul>
              </li>
            )}
            isOptionEqualToValue={(option, value) => option.value === value.value}
          />
        </FormControl>

        {/* Timezone detection info */}
        {detectedInList && formData.timezone === detectedTimezone && (
          <Alert
            severity="info"
            icon={<InfoIcon fontSize="small" />}
            sx={{ py: 0.5 }}
          >
            {t('onboarding.preferences.timezoneDetected', { timezone: detectedTimezone })}
          </Alert>
        )}

        {/* Glucose Unit */}
        <FormControl component="fieldset">
          <FormLabel component="legend">{t('onboarding.preferences.glucoseUnit')}</FormLabel>
          <RadioGroup
            row={!isMobile}
            value={formData.preferredUnit}
            onChange={(e) => updateFormData({ preferredUnit: e.target.value })}
          >
            <FormControlLabel
              value="mmol"
              control={<Radio />}
              label={t('onboarding.preferences.mmolL')}
            />
            <FormControlLabel
              value="mgdl"
              control={<Radio />}
              label={t('onboarding.preferences.mgdL')}
            />
          </RadioGroup>
        </FormControl>

        {/* Language */}
        <FormControl fullWidth>
          <InputLabel id="language-label">{t('onboarding.preferences.language')}</InputLabel>
          <Select
            labelId="language-label"
            value={formData.language}
            label={t('onboarding.preferences.language')}
            onChange={handleLanguageChange}
          >
            <MenuItem value="en">{t('onboarding.preferences.english')}</MenuItem>
            <MenuItem value="sv">{t('onboarding.preferences.swedish')}</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}

export default PreferencesStep;
